/**
 * @fileOverview Behaviors for Transaction Collection Item
 * @name TransactionView
 * @author tsullivan, tbroussard
 */
define([
	'backbone',
	'BaseView',
	'view/txndetails/modules',
	'model/txndetails/modules',
	'lib/fpti-utils'
 ], function (Backbone, BaseView, TransactionDetailsView, TransactionDetailsModel, FPTIUtils) {
	'use strict';

	var TransactionView = BaseView.extend({

		template: 'activity/transactionItemSimple',

		events: {
			'click .js_linkedBlock': 'checkNativeTDSupport',
			'click .js_detailedTable-transactionItem_row': 'invokeDetailsLink',

			//Request money action.
			'click .js_requestRemindAction': 'remindReqMoney',
			'click .js_requestEditAction': 'invokeRequestEditAction',
			'click .js_requestCancelAction': 'invokeRequestCancelAction',

			'click .js_denyAction': 'invokeDenyAction',
			'click .js_acceptAction': 'invokeAcceptAction',
			'click .js_cancelAction': 'invokeCancelAction',

			'click .js_cancelBoletoAction': 'invokeCancelBoletoAction',

			'click .js_payNowAction': 'invokePayNowAction',
			'keydown': 'checkNativeTDSupport'
		},

		/**
		 * Initialize the Transaction View
		 */
		initialize: function (params) {
			this.viewContext = params.viewContext || '';
			this.trackFPTILink = FPTIUtils.createLinkTracker(
				'main:walletweb:' + this.viewContext
			);
			this.displayMode = params.displayMode || 'simple';

			/**
			 * Set view template (passed from Transaction Collection View)
			 * This is necessary because each Transaction View is rendered by this
			 * collection view, without knowing the active display mode by default.
			 */
			if (params && params.displayMode) {
				this.setViewTemplate(params.displayMode);
			}

			// Handle change events on this model's properties (triggered by Transaction Details)
			this.listenTo(this.model, 'change:isExpanded', this.toggleDetailsExpanded);

			// Listen to display mode changes so this View can update its template
			this.listenTo(Backbone, 'displayMode:change', this.setViewTemplate);
		},

		/*
		 * Override BaseView _doRender
		 * Prevents Backbone from wrapping the content in a <div>
		 */
		_doRender: function (content) {
			this.setElement(content);
		},

		/*
		 * Establishes the Transaction Details Model and View and fires an
		 * event with data for the Transaction Collection View.
		 */
		afterRender: function () {
			// Notify Transaction Collection View it's time to render itself.
			Backbone.trigger('transactionItem:viewRendered', this);
		},

		/**
		 * Handle change events to TransactionDetailsModel.isExpanded
		 *
		 * @param model {Object} - Passed because this function is called from event mediation.
		 * @param isExpanded {Boolean} - True if expanded, false if collapsed.
		 */
		toggleDetailsExpanded: function (model, isExpanded) {

			// Add the blue left border && set aria-expanded attribute to track the state of inline transaction details

			if (this.displayMode === 'detailed') {
				this.$el.find('.js_detailedTable-transactionItem_row').toggleClass('highlightTransactionRow', isExpanded);
				this.$el.find('.js_detailedTable-transactionItem_actions').toggleClass('highlightTransactionRow', isExpanded);
				this.$el.attr('aria-expanded', isExpanded);
			} else {
				this.$el.find('.js_transactionItem').toggleClass('highlightTransactionRow', isExpanded);
				this.$el.find('.js_transactionItem').find('.row').attr('aria-expanded', isExpanded);
			}

			// If expand property is set to false, put focus on the main link
			if (!isExpanded) {
				this.$el.find('.js_transactionDetails').attr('aria-busy', false);
				if (this.displayMode === 'detailed') {
					this.$el.focus();
				} else {
					this.$el.find('.js_transactionItem').find('.row').focus();
				}
			}
		},

		/**
		 * Handle Display Mode change events, which requires different templates for
		 * alternative presentation of the same data set.
		 *
		 * @param displayMode {String} - Name of the current display mode setting (simple or detailed)
		 */
		setViewTemplate: function (displayMode) {
			// This only applies to Activity; other views will use the default template.
			if (this.viewContext === 'activity') {
				if (displayMode === 'detailed') {
					this.template = 'activity/transactionItemDetailed';
				} else {
					this.template = 'activity/transactionItemSimple';
				}
			}
		},

		/*
		 * Handles request money remind action
		 */
		remindReqMoney: function(event) {
			event.preventDefault();
			event.stopPropagation();

			var txnAction = { selectedAction: 'remind', state: 'complete'},
				self = this;

			this.setupAction(event, txnAction);

			Backbone.trigger('transactionItem:toggleLoading', this.model.get('id'), true);

			function finalize(response) {
				var txnDetailsModel = self.model.get('transactionDetailsModel');

				if (txnDetailsModel) {
					txnDetailsModel.set('txnAction', txnAction);
					self.model.set('transactionDetailsModel', txnDetailsModel);
				}
				Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
				self.checkNativeTDSupport(event, txnAction);
			}

			function success(response) {
				txnAction.isSuccessful = true;
				finalize(response);
			}

			function error(response) {
				txnAction.isSuccessful = false;
				finalize(response);
			}

			Backbone.ajax({
				type: 'POST',
				url: Backbone.history.options.root + 'transaction/request/remind/' + this.model.get('id') + '/' + this.model.get('extension').group_request_id,
				success: success,
				error: error,
				ignoreErrors: true
			});
		},

		invokeAcceptAction: function(event) {
			var	txnAction = { selectedAction: 'accept', state: 'initiate'},
				self = this,
				txnDetailsModel;

			if (this.shouldRedirectToClassic(txnAction)) {
				return;
			}
			this.setupAction(event, txnAction);

			Backbone.trigger('transactionItem:toggleLoading', this.model.get('id'), true);

			function finalize(response) {
				txnDetailsModel = self.model.get('transactionDetailsModel');
				Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
				txnAction.data = response.data && response.data.action && response.data.action.details;
				if (txnDetailsModel) {
					txnDetailsModel.set('txnAction', txnAction);
					self.model.set('transactionDetailsModel', txnDetailsModel);
				}
				self.checkNativeTDSupport(event, txnAction);
			}

			function success(response) {
				txnAction.isSuccessful = response.data && response.data.action && response.data.action.isSuccessful;
				finalize(response);
			}

			function error(response) {
				txnAction.isSuccessful = false;
				finalize(response);
			}

			Backbone.sync('read', this.model, {
				url: Backbone.history.options.root + 'transaction/pendingPaymentDetails/' + this.model.get('id'),
				success: success,
				error: error
			});
		},

		invokeRequestCancelAction: function(event) {
			var txnAction = { selectedAction: 'requestCancel', state: 'initiate'};

			this.setupAction(event, txnAction);
			this.checkNativeTDSupport(event, txnAction);
		},

		invokeRequestEditAction: function(event) {
			var txnAction = {
				selectedAction: 'requestEdit',
				state: 'initiate',
				invalidAmount: false,
				editAmount: ''
			};

			this.setupAction(event, txnAction);
			this.checkNativeTDSupport(event, txnAction);
		},

		invokeDenyAction: function(event) {
			var txnAction = { selectedAction: 'deny', state: 'initiate'};

			if (this.shouldRedirectToClassic(txnAction)) {
				return;
			}
			this.setupAction(event, txnAction);
			this.checkNativeTDSupport(event, txnAction);
		},

		invokeCancelAction: function(event) {
			var txnAction = { selectedAction: 'cancel', state: 'initiate'};

			if (this.shouldRedirectToClassic(txnAction)) {
				return;
			}
			this.setupAction(event, txnAction);
			this.checkNativeTDSupport(event, txnAction);
		},

		invokeCancelBoletoAction: function(event) {
			var txnAction = { selectedAction: 'boletoCancel', state: 'initiate'};

			this.setupAction(event, txnAction);
			this.checkNativeTDSupport(event, txnAction);
		},

		invokePayNowAction: function(event) {
			var txnAction = { selectedAction: 'payNow', state: 'initiate'};

			if (this.shouldRedirectToClassic(txnAction)) {
				return;
			}
			this.setupAction(event, txnAction);
			this.checkNativeTDSupport(event, txnAction);
		},

		shouldRedirectToClassic: function(txnAction) {
			var actionData = this.model.get('actions') && this.model.get('actions')[txnAction.selectedAction];

			if (actionData) {
				if (actionData.url && actionData.url.indexOf('webscr') > -1) {
					window.open(actionData.url, '_self');
					return true;
				}
			}
		},

		setupAction: function(event, txnAction) {
			event.preventDefault();
			event.stopPropagation();

			var txnDetailsModel = this.model.get('transactionDetailsModel');

			// if inline is not enabled, do not set any model data. Just call checkNativeTDSupport which will open the details in full page.
			if (!this.model.isInlineEnabled()) {
				return this.checkNativeTDSupport(event);
			}

			if (txnDetailsModel) {
				txnDetailsModel.set('txnAction', txnAction);
				txnDetailsModel.set('isRendered', false);
				txnDetailsModel.set('requestedToShow', false);
				this.model.set('transactionDetailsModel', txnDetailsModel);
			}
		},

		/**
		 * Invoke the Inline TD link (Detailed View only)
		 * This handles click events on the transaction row by triggering a subsequent click event
		 * on the transaction details anchor. This is done to accomodate the Detailed View link
		 * behavior while maintaining integrity of existing functionality.
		 *
		 * @param event {Object} - The click event on the transaction row element
		 */
		invokeDetailsLink: function (event) {
			var $targetLink = this.$(event.currentTarget).find('.js_linkedBlock');
			// focus for accessibility, then click link
			$targetLink.focus().click();
		},

		/*
		 * Open Transaction Details View inline or open Classic Transaction Details page
		 * in a new tab, depending on whether the service supports transaction details
		 * for the given transaction type.
		 *
		 * @param event {Object} - The click event on the transaction row element
		 */
		checkNativeTDSupport: function (event, txnAction) {
			/**
			 * Set a flag to check the target of the event since whole row container is clickable
			 * If target/focused item is not link then details will open
			 * If target/focused item is a link and does not have an action flag then do not open the details
			 * If target/focused item is a link and has an action flag details will open and run action
			 */
			var eventTargetIsChildLink = event.target.tagName === 'A' && !txnAction;

			/**
			 * Check enter key event or click
			 */
			if ((event.keyCode && event.keyCode === 13 || !event.keyCode) && !eventTargetIsChildLink) {

				event.preventDefault();
				event.stopPropagation();

				var href = event.currentTarget.getAttribute('data-href') || this.$el.data('href'),
					transactionDetailsModel,
					transactionDetailsView;

				/**
				 * If inline TD is enabled, trigger event to expand the details view for
				 * this transaction; otherwise open classic details in a new window.
				 */
				if (!this.model.isInlineEnabled()) {
					// NOTE: Full page TD view is handle in routes/activity.js
					return window.open(href, '_self');
				}

				/**
				 * add shift click to open TD details full page view
				 * this transaction; otherwise open classic details in a new window.
				 */
				if (event.shiftKey) {
					return window.open(href, '_blank');
				}

				// Instrumentation for 'Pending vs Completed transaction clicks'
				if (this.model.get('status') === 'PENDING') {
					Backbone.trigger('trackLink', null, 'tdClickOnPendingTxn', 'main:walletweb:activity:td:pendingtxn:');
				} else if (this.model.get('status') === 'COMPLETED') {
					Backbone.trigger('trackLink', null, 'tdClickOnCompletedTxn', 'main:walletweb:activity:td:completedtxn:');
				}

				// Instrumentation for 'TD open on which transaction in the list'
				if (this.model && this.model.collection) {
					var indexOfClickedActivity = this.model.collection.indexOf(this.model) + 1,
						pageName = 'main:walletweb:activity:td:' + 'tdClickOnTxnIndex-' + indexOfClickedActivity + ':';
					Backbone.trigger('trackLink', null, 'tdClickOnTxnIndex-' + indexOfClickedActivity, pageName);
				}

				if (!this.model.get('transactionDetailsModel')) {
					transactionDetailsModel = new TransactionDetailsModel({
						id: this.model.get('id')
					});

					if (this.model.get('type') === 'MONEY_REQUEST_SENT' || this.model.get('type') === 'MONEY_REQUEST_RECEIVED') {
						transactionDetailsModel.set('groupRequestId', this.model.get('extension').group_request_id);
						transactionDetailsModel.set('transactionType', 'moneyRequest');
					}

					if (txnAction) {
						transactionDetailsModel.set('txnAction', txnAction);
					}

					if (this.model.get('type') === 'ORDER_PLACED' || this.model.get('type') === 'ORDER_RECEIVED') {
						transactionDetailsModel.set('transactionType', 'order');
					}

					// IN_MINUTES and IN_DAYS are the subtype applicable only for checkCapture transactions
					if (this.model.get('subtype') === 'IN_MINUTES' || this.model.get('subtype') === 'IN_DAYS') {
						transactionDetailsModel.set('transactionType', 'checkCapture');
					}

					transactionDetailsView = new TransactionDetailsView({
						viewContext: this.viewContext,
						model: transactionDetailsModel
					});

					// Bind Transaction Details view to its own sub-view container within the parent view
					transactionDetailsView.setElement(this.$('.js_transactionDetails'));

					// Set transaction details model so we can access its properties
					this.model.set('transactionDetailsModel', transactionDetailsModel);
				}
				Backbone.trigger('transactionDetails:toggleRequestedToShow', this.model.get('id'));
			}
		},
		trackFPTIRedirect: function() {
			var implementedFPTITags = {
				'INVOICE_RECEIVED': 'invoice_rec:view',
				'INVOICE_SENT': 'invoice_sent:view'
			};
			var fptiTag = implementedFPTITags[this.model.get('type')];
			if (fptiTag) {
				this.trackFPTILink(fptiTag);
			}
		}
	});

	return TransactionView;

});



//////////////////
// WEBPACK FOOTER
// ./view/activity/transactionView.js
// module id = 761
// module chunks = 0